import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui';
import { useToast } from '../../../hooks/useToast';
import { useBatchExport } from '../../../hooks/useClustering';
import type { ExportFormat } from '../../../types/clustering';

interface ExportMenuProps {
  batchId: string;
  batchName?: string | null;
  disabled?: boolean;
}

const FORMATS: { key: ExportFormat; label: string }[] = [
  { key: 'csv', label: 'CSV' },
  { key: 'json', label: 'JSON' },
  { key: 'xlsx', label: 'XLSX' },
];

export function ExportMenu({ batchId, batchName, disabled }: ExportMenuProps) {
  const toast = useToast();
  const exportMut = useBatchExport();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleExport = async (format: ExportFormat) => {
    setOpen(false);
    const safeName = (batchName ?? `batch-${batchId.slice(0, 8)}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    try {
      await exportMut.mutateAsync({
        batchId,
        format,
        filename: `${safeName}.${format}`,
      });
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 501 && format === 'xlsx') {
        toast.warning(
          'XLSX export unavailable',
          'openpyxl is not installed on the server. Try CSV or JSON.',
        );
      } else {
        toast.error('Export failed', (err as Error).message);
      }
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        variant="secondary"
        size="md"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || exportMut.isPending}
        loading={exportMut.isPending}
      >
        Export ▾
      </Button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            background: 'var(--color-background-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            minWidth: 140,
            zIndex: 10,
            overflow: 'hidden',
          }}
        >
          {FORMATS.map((f) => (
            <button
              key={f.key}
              role="menuitem"
              type="button"
              onClick={() => handleExport(f.key)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  'var(--color-background-secondary)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              Download {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
